import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { CheckCircle2, Truck, PartyPopper } from 'lucide-react';
import { WhatsAppIcon, waLink } from './Effects';
import { useStore } from '../context/StoreContext';
import { CURRENCY } from '../mock/mock';
import { toast } from 'sonner';

const CheckoutModal = ({ open, onClose }) => {
  const { totals, cartDetailed, placeOrder } = useStore();
  const [form, setForm] = useState({ name: '', phone: '', address: '', city: '', pincode: '' });
  const [placedOrder, setPlacedOrder] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address || !form.city || !form.pincode) {
      toast.error('Please fill all delivery details.');
      return;
    }
    if (!/^\d{10}$/.test(form.phone)) {
      toast.error('Please enter a valid 10-digit phone number.');
      return;
    }
    // open tab synchronously so popup blockers allow the auto WhatsApp alert
    const waWin = window.open('', '_blank');
    try {
      const order = await placeOrder(form);
      setPlacedOrder(order);
      toast.success('Order placed! Opening WhatsApp to alert our team...');
      if (waWin) waWin.location.href = orderLink(order);
      else window.location.href = orderLink(order);
    } catch (err) {
      if (waWin) waWin.close();
      toast.error('Could not place order. Please try again.');
    }
  };

  const orderLink = (order) => {
    const lines = order.items.map((i) => `• ${i.qty} x ${i.name} (${i.size}) - ${CURRENCY}${i.unitPrice * i.qty}`).join('\n');
    return waLink(
      `*New FBD Cleaning Order*\n` +
      `Order ID: ${order.id}\n` +
      `Name: ${order.customer.name}\n` +
      `Phone: ${order.customer.phone}\n` +
      `Address: ${order.customer.address}, ${order.customer.city} - ${order.customer.pincode}\n\n` +
      `${lines}\n\n` +
      `Total (COD): ${CURRENCY}${order.total}`
    );
  };

  const whatsappConfirm = (order) => window.open(orderLink(order), '_blank');

  const handleClose = () => {
    setPlacedOrder(null);
    setForm({ name: '', phone: '', address: '', city: '', pincode: '' });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-lg">
        {placedOrder ? (
          <div className="text-center py-4">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
              <PartyPopper className="h-8 w-8 text-primary" />
            </div>
            <DialogTitle className="font-display font-extrabold text-2xl mt-4 text-foreground">Order Confirmed!</DialogTitle>
            <p className="text-muted-foreground mt-1">Thank you, {placedOrder.customer.name}. Your order is on its way.</p>
            <div className="mt-5 bg-secondary/40 rounded-xl p-4 text-left space-y-2">
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Order ID</span><span className="font-semibold">{placedOrder.id}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Items</span><span className="font-semibold">{placedOrder.items.reduce((s, i) => s + i.qty, 0)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Payment</span><span className="font-semibold">Cash on Delivery</span></div>
              <div className="flex justify-between text-base font-display font-bold pt-2 border-t border-border"><span>Total Payable</span><span className="text-primary">{CURRENCY}{placedOrder.total}</span></div>
            </div>
            <p data-testid="order-whatsapp-alert-note" className="mt-4 text-xs text-muted-foreground">WhatsApp opened with your order details — just tap <span className="font-semibold">Send</span> to alert our team instantly.</p>
            <Button data-testid="order-whatsapp-resend-btn" onClick={() => whatsappConfirm(placedOrder)} className="btn-shimmer mt-3 w-full bg-[#25D366] hover:bg-[#1eb355] text-white font-semibold py-6">
              <WhatsAppIcon className="h-5 w-5 mr-2" /> Send Order on WhatsApp
            </Button>
            <Button data-testid="order-continue-shopping-btn" onClick={handleClose} variant="outline" className="mt-2 w-full">Continue Shopping</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2"><Truck className="h-5 w-5 text-primary" /> Delivery Details</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cname">Full Name</Label>
                  <Input id="cname" data-testid="checkout-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" placeholder="Your name" />
                </div>
                <div>
                  <Label htmlFor="cphone">Phone</Label>
                  <Input id="cphone" data-testid="checkout-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" placeholder="10-digit number" />
                </div>
              </div>
              <div>
                <Label htmlFor="caddr">Address</Label>
                <Textarea id="caddr" data-testid="checkout-address-input" rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" placeholder="House no, street, area" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="ccity">City</Label>
                  <Input id="ccity" data-testid="checkout-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5" placeholder="City" />
                </div>
                <div>
                  <Label htmlFor="cpin">Pincode</Label>
                  <Input id="cpin" data-testid="checkout-pincode-input" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} className="mt-1.5" placeholder="6-digit pincode" />
                </div>
              </div>

              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                <p className="text-sm text-foreground/80">We currently accept <span className="font-semibold">Cash on Delivery (COD)</span> only. Pay when your order arrives.</p>
              </div>

              <div className="flex justify-between font-display font-extrabold text-lg">
                <span>Total ({cartDetailed.reduce((s, l) => s + l.qty, 0)} items)</span>
                <span className="text-primary">{CURRENCY}{totals.total}</span>
              </div>
              <Button data-testid="place-order-btn" type="submit" className="btn-shimmer w-full bg-primary hover:bg-primary/90 text-white font-semibold py-6">Place Order (COD)</Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CheckoutModal;
